import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import StepperIndicator from "@/components/ui/stepper-indicator";
import { toast } from "@/components/ui/use-toast";
import { ErrorType, Workout } from "@/types";
import {
  useAddWorkoutMutation,
  useUpdateWorkoutMutation,
  useDeleteWorkoutMutation,
  useVerifyWorkoutCreationMutation,
} from "@/services/workoutApi";
import { processAndUploadImages } from "@/lib/constants/workout";
import { PublishConfirmDialog } from "../components/workout-warning";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import WorkoutStep1 from "./workout-step-1";
import WorkoutStep2 from "./workout-step-2";

interface EmbeddedWorkoutWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the workout is successfully saved (published or draft) */
  onComplete?: (workoutId: number) => void;
  /** Pre-selected goal label from consultation (e.g., "Build Muscle") */
  initialGoal?: string;
  /** Pre-selected category ID when coach has exactly one category */
  initialCategoryId?: number;
  /** All category IDs from the coach to filter the category dropdown */
  categoryIds?: number[];
}

const LAST_STEP = 2;

const EmbeddedWorkoutWizard = ({
  isOpen,
  onClose,
  onComplete,
  initialGoal,
  initialCategoryId,
  categoryIds,
}: EmbeddedWorkoutWizardProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const initialValue: Partial<Workout> = {
    workout_name: "",
    org_id: orgId,
    description: "",
    visible_for: undefined,
    goals: "",
    level: "",
    weeks: undefined,
    img_url: undefined,
    members: [],
    file: undefined,
    category_id: undefined,
  };

  const form = useForm<Workout>({
    defaultValues: initialValue,
    mode: "all",
  });

  // ── Pre-fill goal and category from consultation when wizard opens ────────
  useEffect(() => {
    if (isOpen) {
      if (initialGoal) {
        form.setValue("goals", initialGoal, { shouldValidate: true, shouldDirty: true });
      }
      if (initialCategoryId) {
        form.setValue("category_id", initialCategoryId, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [isOpen, initialGoal, initialCategoryId, form]);

  const [activeStep, setActiveStep] = useState(1);
  const [workoutIdState, setWorkoutIdState] = useState<number | null>(null);
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [createWorkout, { isLoading: isCreating }] = useAddWorkoutMutation();
  const [updateWorkout, { isLoading: isUpdating }] = useUpdateWorkoutMutation();
  const [deleteWorkout, { isLoading: isDeleting }] = useDeleteWorkoutMutation();
  const [verifyWorkout, { isLoading: isVerifying }] =
    useVerifyWorkoutCreationMutation();

  // ── Step 1 submit: create / update workout info ───────────────────────────
  const handleStep1Submit = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast({
        variant: "destructive",
        description: "Please fill all mandatory fields",
      });
      return;
    }

    setIsSubmittingStep1(true);
    try {
      const data = form.getValues();
      const fileInputObject = { file: data.file };
      const existingImages = { file: data.img_url };
      const result = await processAndUploadImages(fileInputObject, existingImages);

      const payload = {
        ...data,
        img_url: result.img_url,
        org_id: orgId,
        workout_name: data.workout_name?.toLowerCase() ?? "",
      };
      delete (payload as any).file;

      let newId: number;

      if (workoutIdState) {
        const resp = await updateWorkout({ id: workoutIdState, ...payload }).unwrap();
        newId = resp.workout_id ?? workoutIdState;
      } else {
        const resp = await createWorkout(payload as Workout).unwrap();
        if (!resp?.id) {
          toast({ variant: "destructive", description: "Failed to create workout" });
          return;
        }
        newId = resp.id;
      }

      setWorkoutIdState(newId);
      setActiveStep(2);
    } catch (error: unknown) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error saving",
        description:
          typedError?.data?.detail ||
          (typedError?.data as { message?: string })?.message ||
          "Something went wrong",
      });
    } finally {
      setIsSubmittingStep1(false);
    }
  };

  // ── Save as Published ─────────────────────────────────────────────────────
  const handleVerify = async () => {
    if (!workoutIdState) return;
    try {
      const verifyData = await verifyWorkout({
        id: workoutIdState,
        org_id: orgId,
      }).unwrap();

      if (verifyData?.status === 200) {
        toast({ variant: "success", description: "Workout saved successfully!" });
        setIsDialogOpen(false);
        onComplete?.(workoutIdState);
        handleReset();
        onClose();
      } else {
        toast({
          variant: "destructive",
          description: "Each day must have at least one exercise and one day.",
        });
      }
    } catch (error: unknown) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        description:
          typedError?.data?.message ||
          typedError?.data?.detail ||
          "Verification failed. Please try again.",
      });
    }
  };

  // ── Save as Draft ─────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!workoutIdState || isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      const currentData = form.getValues();
      await updateWorkout({
        id: workoutIdState,
        workout_name: currentData.workout_name,
        description: currentData.description,
        goals: currentData.goals,
        img_url: currentData.img_url,
        level: currentData.level,
        visible_for: currentData.visible_for,
        weeks: currentData.weeks,
        members: currentData.members,
        org_id: orgId,
        status: "draft",
      }).unwrap();

      toast({ variant: "success", description: "Workout saved as draft" });
      setIsDialogOpen(false);
      onComplete?.(workoutIdState);
      handleReset();
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Failed to save as draft. Please try again.",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  // ── Cancel: delete the draft workout if it was already created ────────────
  const handleCancel = async () => {
    if (workoutIdState) {
      try {
        await deleteWorkout({ workoutid: workoutIdState, org_id: orgId }).unwrap();
      } catch {
        // Ignore delete errors on cancel
      }
    }
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setActiveStep(1);
    setWorkoutIdState(null);
    form.reset(initialValue);
  };

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <SheetContent
          className="w-full !max-w-full sm:!max-w-[1050px] py-0 custom-scrollbar h-full overflow-y-auto"
          hideCloseButton
        >
          <SheetHeader className="sticky z-40 top-0 py-4 bg-white">
            <SheetTitle>
              <div className="flex flex-wrap justify-between gap-3 items-start">
                <div>
                  <p className="font-semibold text-base sm:text-lg">
                    Create Workout
                  </p>
                  <div className="text-sm text-gray-400 font-medium">
                    Step {activeStep} of {LAST_STEP}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  {/* Cancel */}
                  <LoadingButton
                    type="button"
                    className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                    variant="outline"
                    onClick={handleCancel}
                    loading={isDeleting}
                    disabled={isSubmittingStep1 || isCreating || isUpdating || isDeleting}
                  >
                    <i className="fa fa-xmark"></i>
                    <span className="hidden sm:inline">Cancel</span>
                  </LoadingButton>

                  {/* Previous (Step 2 only) */}
                  {activeStep > 1 && (
                    <Button
                      className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                      type="button"
                      variant="outline"
                      onClick={() => setActiveStep((s) => s - 1)}
                    >
                      <i className="fa fa-arrow-left-long"></i>
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                  )}

                  {/* Next (Step 1) / Save dropdown (Step 2) */}
                  {activeStep < LAST_STEP ? (
                    <LoadingButton
                      type="button"
                      className="bg-primary text-black h-9 px-3 sm:w-[120px] flex items-center gap-2"
                      onClick={handleStep1Submit}
                      loading={isSubmittingStep1 || isCreating || isUpdating}
                      disabled={isSubmittingStep1 || isCreating || isUpdating}
                    >
                      {!(isSubmittingStep1 || isCreating || isUpdating) && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      Next
                    </LoadingButton>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          className="w-[130px] bg-primary text-black flex items-center gap-2"
                          disabled={isVerifying || isSavingDraft}
                        >
                          <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                          Save
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[200px]">
                        <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
                          Save as Published
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleSaveDraft}>
                          Save as Draft
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </SheetTitle>
            <Separator className="h-[1px] rounded-full my-2" />
          </SheetHeader>

          {/* Stepper */}
          <div className="flex justify-center mt-5">
            <div className="w-full sm:w-1/2 flex justify-center">
              <StepperIndicator
                activeStep={activeStep}
                labels={[
                  { key: 1, label: "Plan Information" },
                  { key: 2, label: "Training & Exercise" },
                ]}
                lastKey={LAST_STEP}
              />
            </div>
          </div>

          {/* Step content — rendered directly, no Outlet needed */}
          <div className="px-4 pb-10">
            {activeStep === 1 && <WorkoutStep1 form={form} categoryIds={categoryIds} />}
            {activeStep === 2 && workoutIdState && (
              <WorkoutStep2 workoutId={workoutIdState} />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <PublishConfirmDialog
        isLoading={isVerifying}
        onDialogClose={() => setIsDialogOpen(false)}
        isOpen={isDialogOpen}
        onVerify={handleVerify}
        onCancel={() => setIsDialogOpen(false)}
      />
    </>
  );
};

export default EmbeddedWorkoutWizard;
